import { create } from "zustand";

// The seam between the API layer (`lib/api.ts`, which raises MfaRequiredError)
// and ProAuthGate (which owns the UI). Module-level on purpose: a 403 can come
// from ANY request, long after the gate's first probe, and the gate must hear
// about it (spec 7.2).
//
// While a gate is mounted, `apiRequest` parks the failing request here; the
// gate runs the remedy (enrol or reverify) and `settle`s; the request then
// retries exactly once with a fresh token. With no gate mounted (preview,
// tests) nothing waits and the typed error simply propagates.

export type MfaTrigger = "acquisition" | "has_clients";

export type MfaReverificationLevel = {
  afterMinutes?: number;
  level?: string;
};

type Status = "idle" | "required" | "blocked";

type MfaGateState = {
  status: Status;
  trigger: MfaTrigger | null;
  /** Reverification level from the 403's Clerk hint, when present. */
  reverification: MfaReverificationLevel | null;
  /** Bumps on every new run of the remedy; the gate keys its flow on it. */
  episode: number;
};

const INITIAL: MfaGateState = {
  status: "idle",
  trigger: null,
  reverification: null,
  episode: 0,
};

export const useMfaGate = create<MfaGateState>(() => INITIAL);

let mounted = 0;
let waiters: Array<(ok: boolean) => void> = [];

type RaiseInfo = {
  trigger: MfaTrigger;
  reverification?: MfaReverificationLevel | null;
};

export const mfaGate = {
  /** The gate registers itself; returns the unregister function. */
  mount(): () => void {
    mounted += 1;
    return () => {
      mounted = Math.max(0, mounted - 1);
    };
  },

  isMounted(): boolean {
    return mounted > 0;
  },

  /**
   * A request failed with mfa_required. Resolves true when the remedy
   * completed (retry the request), false when cancelled/failed or no gate is
   * mounted. Concurrent raises share one episode.
   */
  raise(info: RaiseInfo): Promise<boolean> {
    if (mounted === 0) return Promise.resolve(false);
    const state = useMfaGate.getState();
    if (state.status === "idle") {
      useMfaGate.setState({
        status: "required",
        trigger: info.trigger,
        reverification: info.reverification ?? null,
        episode: state.episode + 1,
      });
    } else if (state.status === "blocked") {
      // A stable blocked state is not re-entered by stray requests: fail them.
      return Promise.resolve(false);
    } else if (state.trigger === "acquisition" && info.trigger === "has_clients") {
      // A has_clients refusal joining an open acquisition prompt upgrades the
      // episode: "Not now" must not also reject a request that cannot be
      // skipped. Never downgrades; the remedy stays fixed per episode and the
      // per-episode analytics (keyed on the episode) do not re-fire.
      useMfaGate.setState({ trigger: "has_clients" });
    }
    return new Promise<boolean>((resolve) => waiters.push(resolve));
  },

  /** A retried request was refused again: stop, show the stable state. */
  block(info: RaiseInfo): void {
    if (mounted === 0) return;
    const state = useMfaGate.getState();
    if (state.status === "blocked") return;
    useMfaGate.setState({
      status: "blocked",
      trigger:
        state.trigger === "has_clients" ? "has_clients" : info.trigger,
      reverification: info.reverification ?? state.reverification,
    });
  },

  /**
   * The gate finished its remedy. Returns whether a parked request was
   * waiting (if not, the caller must re-probe itself).
   */
  settle(ok: boolean): boolean {
    const pending = waiters;
    waiters = [];
    if (ok) {
      useMfaGate.setState({ status: "idle", trigger: null, reverification: null });
    } else {
      useMfaGate.setState({ status: "blocked" });
    }
    pending.forEach((resolve) => resolve(ok));
    return pending.length > 0;
  },

  /**
   * "Not now" at an acquisition prompt: fail every parked request (its caller
   * surfaces its normal error) and return to idle so the shell comes back.
   * Never re-probes.
   */
  dismiss(): void {
    const pending = waiters;
    waiters = [];
    useMfaGate.setState({ status: "idle", trigger: null, reverification: null });
    pending.forEach((resolve) => resolve(false));
  },

  /** Retry button on the blocked state: run the remedy again. */
  retry(): void {
    const state = useMfaGate.getState();
    if (state.status !== "blocked") return;
    useMfaGate.setState({ status: "required", episode: state.episode + 1 });
  },

  reset(): void {
    mounted = 0;
    waiters = [];
    useMfaGate.setState(INITIAL);
  },
};

/** A fresh Clerk session token (bypasses the cache: `fva` just changed). */
export async function freshSessionToken(): Promise<string | null> {
  const clerk = (
    globalThis as {
      Clerk?: {
        session?: {
          getToken: (o?: { skipCache?: boolean }) => Promise<string | null>;
        } | null;
      };
    }
  ).Clerk;
  if (!clerk?.session) return null;
  try {
    return await clerk.session.getToken({ skipCache: true });
  } catch {
    return null;
  }
}
